'use client';

import React from 'react';
import { useScrollLock } from '@repo/ui/src/hooks/useScrollLock';
import { UIContext } from '@repo/ui/src/modules/UI/context/UIContext';
import { ListenerCB, ModalID, OpenModalsType, PushModal, ToggleListenerFunc } from '../../';

export interface UIProviderProps {
  children: React.ReactNode;
}

const getLastKeyInMap = <K, V>(map: Map<K, V>) => [...map][map.size - 1][0];

export const UIProvider = ({ children }: UIProviderProps) => {
  const eventListeners = React.useRef<Map<ModalID, Set<ListenerCB>>>(new Map());
  const [disabledIDsSet, setDisabledIDsSet] = React.useState<Set<ModalID>>(() => new Set());
  const [openModalsMap, setOpenModalsMap] = React.useState<OpenModalsType>(() => new Map());
  const { lock, unlock } = useScrollLock();

  const ids = React.useMemo(() => [...(openModalsMap ?? [])].map(([key]) => key), [openModalsMap]);

  const closeModal = React.useCallback(
    (id?: ModalID) =>
      setOpenModalsMap((curr) => {
        const newMap = new Map(curr);
        if (id) {
          const index = ids.indexOf(id);
          ids.slice(index).forEach((_id) => {
            eventListeners.current.get(_id)?.forEach((cb) => cb(false));

            newMap.delete(_id);
          });
        } else {
          const _id = getLastKeyInMap(newMap);
          eventListeners.current.get(_id)?.forEach((cb) => cb(false));
          newMap.delete(_id);
        }
        return newMap;
      }),
    [ids],
  );

  const pushModal = React.useCallback<PushModal>(
    (id, data = undefined) => {
      if (disabledIDsSet.has(id)) {
        return;
      }
      setOpenModalsMap((curr) => {
        eventListeners.current.get(id)?.forEach((cb) => cb(true));
        const newMap = new Map(curr);
        newMap.set(id, data);
        return newMap;
      });
    },
    [disabledIDsSet],
  );

  const disableID = React.useCallback((id: ModalID) => {
    setDisabledIDsSet((curr) => {
      const newSet = new Set(curr);
      newSet.add(id);
      return newSet;
    });
  }, []);

  const enableID = React.useCallback(
    (id: ModalID) =>
      setDisabledIDsSet((curr) => {
        const newSet = new Set(curr);
        newSet.delete(id);
        return newSet;
      }),
    [],
  );

  const removeListener = React.useCallback<ToggleListenerFunc>((id, func) => {
    eventListeners.current.get(id)?.delete(func);
  }, []);

  const addListener = React.useCallback<ToggleListenerFunc>(
    (id, func) => {
      if (!eventListeners.current.has(id)) {
        eventListeners.current.set(id, new Set());
      }
      const listenerGroup = eventListeners.current.get(id);
      listenerGroup!.add(func);
      return () => removeListener(id, func);
    },
    [removeListener],
  );

  React.useEffect(() => {
    if (openModalsMap?.size) {
      lock();
    } else {
      unlock();
    }
  }, [openModalsMap, lock, unlock]);

  return (
    <UIContext.Provider
      value={{
        openModalIds: ids,
        closeModal,
        pushModal,
        disableID,
        enableID,
        openModalsMap,
        addListener,
        removeListener,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};
